import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Sparkles } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { InsightMeta, isRecentlyPublished } from "@/data/insights";
import { cn } from "@/lib/utils";

interface InsightCardProps {
  insight: InsightMeta;
  variant?: "default" | "featured";
}

export const InsightCard = ({ insight, variant = "default" }: InsightCardProps) => {
  // Dynamically get the icon component
  const IconComponent = (LucideIcons as any)[insight.icon] || Calendar;
  const showNewBadge = isRecentlyPublished(insight);
  
  if (variant === "featured") {
    return (
      <Link 
        to={`/insights/${insight.slug}`}
        className="group block"
        onClick={() => window.scrollTo(0, 0)}
      >
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/80 p-6 sm:p-8 md:p-10 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/20">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
          
          {/* Badge */}
          <div className="relative z-10 flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
              <Sparkles className="w-3 h-3" />
              Latest Insight
            </span>
            {showNewBadge && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white text-primary uppercase tracking-wider">
                New
              </span>
            )}
          </div>
          
          <div className="relative z-10 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 text-white/70 text-sm mb-3">
                <IconComponent className="w-4 h-4" />
                <span>{insight.tag}</span>
              </div>
              
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                {insight.title}
              </h2>
              <p className="text-white/80 text-base sm:text-lg mb-4">
                {insight.subtitle}
              </p>
              <p className="text-white/60 text-sm leading-relaxed max-w-xl">
                {insight.description}
              </p>
              
              <div className="flex items-center gap-4 mt-6">
                <span className="flex items-center gap-1.5 text-white/60 text-sm">
                  <Clock className="w-4 h-4" />
                  {insight.readTime}
                </span>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex flex-row md:flex-col gap-4 md:justify-center">
              {insight.stats.map((stat, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 md:px-5 md:py-4">
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative z-10 mt-6 flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
            <span>Read full insight</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </Link>
    );
  }

  // Coming soon variant
  if (insight.comingSoon) {
    return (
      <div className="group block cursor-default">
        <div className="data-card h-full flex flex-col opacity-70">
          <div className="flex items-start justify-between gap-3 mb-3">
            <span className="tag">
              <span className="tag-dot" />
              {insight.tag}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-muted text-muted-foreground uppercase tracking-wider">
              Coming Soon
            </span>
          </div>
          
          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-4">
            <IconComponent className="w-5 h-5 text-muted-foreground" />
          </div>
          
          <h3 className="font-display text-lg font-semibold text-muted-foreground mb-2">
            {insight.title}
          </h3>
          <p className="text-sm text-muted-foreground/70 mb-4 flex-1">
            {insight.description}
          </p>
          
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <span className="text-xs text-muted-foreground/70 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {insight.readTime}
            </span>
            <span className="text-sm text-muted-foreground">
              Coming soon
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link 
      to={`/insights/${insight.slug}`}
      className="group block"
      onClick={() => window.scrollTo(0, 0)}
    >
      <div className="data-card h-full flex flex-col hover:shadow-lg hover:border-primary/30 transition-all duration-300">
        <div className="flex items-start justify-between gap-3 mb-3">
          <span className="tag">
            <span className="tag-dot" />
            {insight.tag}
          </span>
          {showNewBadge && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary uppercase tracking-wider">
              New
            </span>
          )}
        </div>
        
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
          <IconComponent className="w-5 h-5 text-primary" />
        </div>
        
        <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
          {insight.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 flex-1">
          {insight.description}
        </p>
        
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {insight.readTime}
          </span>
          <span className="text-sm font-medium text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
            Read
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};
